import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { Search, UserCheck, UserX, Shield, Trash2 } from 'lucide-react';

const ROLE_LABELS = { owner: 'مالك', renter: 'مستأجر', both: 'مالك ومستأجر', admin: 'مشرف' };
const ROLE_COLORS = { owner: 'text-blue-400 bg-blue-500/10', renter: 'text-green-400 bg-green-500/10', both: 'text-purple-400 bg-purple-500/10', admin: 'text-amber-400 bg-amber-500/10' };

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');

  const { data, isLoading } = useQuery(['admin-users', page, search, roleFilter], () => {
    const p = new URLSearchParams({ page, limit: 20 });
    if (search) p.set('search', search);
    if (roleFilter) p.set('role', roleFilter);
    return api.get(`/admin/users?${p}`).then(r => r.data);
  }, { keepPreviousData: true });

  const toggleMutation = useMutation(
    ({ id, is_active }) => api.put(`/admin/users/${id}`, { is_active }),
    { onSuccess: () => queryClient.invalidateQueries('admin-users') }
  );

  const users = data?.users || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">إدارة المستخدمين</h1>
        <span className="text-slate-400 text-sm">{data?.total || 0} مستخدم</span>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="form-input w-full pr-9 text-sm py-2.5"
            placeholder="ابحث بالاسم أو البريد أو الهاتف..."
          />
        </div>
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }} className="form-input text-sm py-2.5">
          <option value="">جميع الأنواع</option>
          {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/50 text-slate-400">
                <th className="text-right py-3 px-4 font-medium">المستخدم</th>
                <th className="text-right py-3 px-4 font-medium">النوع</th>
                <th className="text-right py-3 px-4 font-medium">الهاتف</th>
                <th className="text-right py-3 px-4 font-medium">المدينة</th>
                <th className="text-right py-3 px-4 font-medium">الحالة</th>
                <th className="text-right py-3 px-4 font-medium">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">جارٍ التحميل...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">لا توجد نتائج</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-b border-slate-700/20 hover:bg-slate-700/20 transition-colors">
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-white font-medium">{u.full_name}</p>
                      <p className="text-slate-500 text-xs">{u.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role]}`}>
                      {ROLE_LABELS[u.role]}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-400">{u.phone || '—'}</td>
                  <td className="py-3 px-4 text-slate-400">{u.city || '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {u.is_active ? 'نشط' : 'موقوف'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleMutation.mutate({ id: u.id, is_active: !u.is_active })}
                        className={`p-1.5 rounded-lg transition-colors ${u.is_active ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'}`}
                        title={u.is_active ? 'تعليق' : 'تفعيل'}
                      >
                        {u.is_active ? <UserX size={14} /> : <UserCheck size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700/50">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn-secondary text-sm py-1.5 disabled:opacity-40">السابق</button>
            <span className="text-slate-400 text-sm">صفحة {page} من {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="btn-secondary text-sm py-1.5 disabled:opacity-40">التالي</button>
          </div>
        )}
      </div>
    </div>
  );
}
